<script lang="ts" setup>
	import { useSeoSchema } from '~/utils/seo-schema'
	import { ProductCode } from '~/types'
	import type { H1Config } from '~/components/ui/h1.vue'
	import type { FAQContext } from '~/components/main/faq.vue'
	import type { teamBenefitsContext } from '~/components/main/productivity-benefits.vue'
	import type { GuideListContext } from '~/components/molecules/important-guides.vue'
	import type { ProcessesStepContext } from '~/components/molecules/process-steps.vue'

	definePageMeta({
		layout: 'default'
	})
	const seo = {
		title: 'MyCoverGenius | Mini-Comprehensive | Auto insurance for businesses',
		description:
			'Auto insurance with all the benefits of the regular 3rd party cover but also covers your own vehicle to a limit of ₦500,000 in the event of an accident for just ₦25,000 per annum',
		tags: ['Auto insurance, 3rd party cover'],
		url: `${import.meta.env.VITE_BUSINESS_URL}/mini-comprehensive`
	}
	useSeoMeta({
		title: seo.title,
		description: seo.description,
		ogTitle: seo.title,
		ogDescription: seo.description,
		ogType: 'website',
		ogLocale: 'en_NG',
		articleTag: seo.tags,
		ogUrl: seo.url,
		twitterTitle: seo.title,
		twitterDescription: seo.description,
		twitterSite: '@mycovergenius',
		twitterCard: 'summary_large_image',
		twitterAppUrlGoogleplay: seo.url,
		twitterAppUrlIphone: seo.url,
		robots: {
			all: 'true',
			follow: 'true',
			index: 'true'
		}
	})
	const isDialogOpen = shallowRef(false)
	const showBenefits = shallowRef(false)
	const { navigateToProduct, viewMoreProducts } = useProductsStore()

	// const productInfo = computed(() => useProductsStore().filterProductByCode(ProductCode.MINI_COMPREHENSIVE))
	const benefitHighlights = [
		{
			title: 'Get covered',
			description:
				'Getting auto Insurance shouldn’t waste time. You receive your insurance certificate instantly.',
			image: '/v1727082981/ivc_l9sudc.webp',
			open: true
		},
		{
			title: 'Fast claims',
			description:
				'Claims settlement is digital, fast, seamless, and transparent powered by our A.I claims solution.',
			image: '/v1727082984/qcp_oxiunb.webp'
		},
		{
			title: 'Flexible',
			description:
				'You’ve already spent a lot on your vehicles. You shouldn’t have to pay so much to protect them.',
			image: '/v1727082981/auto-bfo_gezk97.webp'
		},
		{
			title: 'Valid insurance policies',
			description:
				'Our auto insurance policies are licensed and genuine. Once the purchase is completed, the policy is automatically uploaded on the NIID database, hence assuring verification of certificate by law enforcement officials.',
			image: '/v1727082982/inu_xwtqqi.webp'
		},
		{
			title: 'Bulk processing',
			description:
				"This feature enables you to upload your entire fleet's information in one go, saving you time and effort.",
			image: '/v1727082967/highlight-4_rr0xhn.webp'
		},
		{
			title: 'Delegate like a genius',
			description: 'Assign roles and permissions to trusted teammates. Share the control, not the stress.',
			image: '/v1727082977/oan_gqspqy.webp'
		},
		{
			title: 'Analytics and insights',
			description:
				'View transaction history from your account dashboard, including premium payments, employee utilisation, and renewals.',
			image: '/v1727082982/cph_e6lxub.webp'
		},
		{
			title: 'Employee Mobile App',
			description: 'Protect yourself and your vehicle from accidents, all in just a few taps.',
			image: '/v1761841931/mobile-app-comms_opxlgb.webp'
		}
	]
	const faqList = ref<FAQContext[]>([
		{
			question: 'What are the benefits of Mini Comprehensive Auto?',
			answer: 'It provides coverage for third-party bodily injury (unlimited), third-party property damage (up to ₦3,000,000), and accidental damage to your vehicle (up to ₦500,000).',
			open: true
		},
		{
			question: 'Does the Mini Comprehensive cover vehicle theft?',
			answer: 'Does the Mini Comprehensive cover vehicle theft? No, it does not cover theft but covers other third party related damages.',
			open: false
		}
	])

	const { generateProductSchema, generateFaqSchema, generateBreadcrumbSchema } = useSeoSchema()

	useHead({
		script: [
			{
				type: 'application/ld+json',
				innerHTML: JSON.stringify(
					generateProductSchema({
						name: 'Mini Comprehensive',
						description: seo.description,
						image: 'https://res.cloudinary.com/dzhhy3ovo/image/upload/v1727082911/bbo_twyjxx.webp',
						url: seo.url,
						price: '25000'
					})
				)
			},
			{ type: 'application/ld+json', innerHTML: JSON.stringify(generateFaqSchema(faqList.value)) },
			{
				type: 'application/ld+json',
				innerHTML: JSON.stringify(
					generateBreadcrumbSchema([
						{ name: 'Home', item: 'https://mycovergenius.com/' },
						{ name: 'Mini Comprehensive', item: seo.url }
					])
				)
			}
		],
		link: [{ rel: 'canonical', href: seo.url }]
	})

	const onboardingProcesses = shallowRef<ProcessesStepContext>([
		{
			icon: {
				dir: 'random',
				icon: 'auto-wishlist',
				height: '80',
				width: '80'
			},
			title: 'Vehicle details',
			description: 'Fill in the required details to <br class="md:inline hidden" /> register your vehicles.'
		},
		{
			icon: {
				dir: 'random',
				icon: 'auto-card',
				height: '140',
				width: '140'
			},
			title: 'Make payment',
			description:
				'Once your vehicle details are entered, <br /> proceed to make the payment for insurance <br /> coverage and complete your purchase.'
		},
		{
			icon: {
				dir: 'random',
				icon: 'travel-umbrella',
				height: '80',
				width: '80'
			},
			title: 'Get certificate instantly',
			description:
				'After completing the payment and <br /> activation process, receive your insurance <br /> certificate via email.'
		}
	])
	const btnClass: H1Config = {
		tag: { base: 'bg-main text-white sm:hidden' },
		wrapperClass: {
			alignItems: '',
			overflow: '',
			maxWidth: 'w-full'
		},
		hClass: {
			wrapper: {
				size: 'text-[2.75rem]/[3.135rem] -tracking-[0.0825rem] md:text-[3.625rem]/[3.83rem] md:-tracking-[0.10rem] lg:text-[4.25rem]/[4.5rem] lg:-tracking-[0.135rem]',
				alignItems: '',
				base: 'text-wrap'
			}
		},
		paragraphClass: {
			maxWidth: 'max-w-[586px]',
			text: 'xl:text-xl text-base md:text-lg',
			base: 'mt-5',
			leading: 'md:leading-[32px]'
		},
		btn: {
			base: 'sm:mt-20',
			primary: {
				btn: {
					base: 'bg-white  text-base text-auto-800 hover:text-white',
					onHoverPseudo: 'hover:before:bg-auto-500'
				},
				text: {
					size: 'text-sm sm:text-base'
				}
			},
			secondary: {
				base: 'default-btn  max-w-[405px] text-center justify-center w-full gap-x-3',
				text: 'text-sm md:text-base'
			}
		}
	}
	const guideList: GuideListContext = [
		{
			tab: 'claim_process',
			label: 'Simple. Fast. Transparent.',
			description: "That's our vehicle insurance claims process in three words. Here's how it works",
			list: [
				{
					iconTag: {
						dir: 'random',
						icon: 'clipboard-text',
						height: 16
					},
					title: 'Report Claim',
					description:
						'File your claim instantly from your dashboard. No need for endless calls or paperwork.',
					imageSrc: '/v1727082821/auto-claim_hsfvml.gif',
					show: true
				},
				{
					title: 'Claims assessment',
					description: 'Your claim is quickly reviewed to ensure a smooth process.',
					iconTag: {
						dir: 'random',
						icon: 'clock'
					},
					imageSrc: '/v1727082834/auto-claim-2_rlizty.gif'
				},
				{
					title: 'Instant payout',
					description:
						"Got the green light? Enjoy speedy payouts for approved claims – because getting back on the road shouldn't be a wait.",
					iconTag: {
						dir: 'random',
						icon: 'receipt'
					},
					imageSrc: '/v1727082957/gadget-claim-3_rals23.webp'
				}
			]
		}
	]
	const teamBenefits: teamBenefitsContext = [
		{
			title: 'Own vehicle damage up to <br /> ₦500,000',
			description:
				'Receive comprehensive coverage for accidental damage to your vehicles, ensuring financial protection for repairs or replacements up to ₦500,000.',
			icon: {
				icon: 'car-accident',
				dir: 'random'
			}
		},
		{
			title: 'Unlimited 3rd-party bodily <br /> injury coverage',
			description:
				"Rest assured knowing that you're protected against any liability for bodily injury to third parties, with no limit on the coverage amount.",
			icon: {
				icon: 'auto-injury',
				dir: 'random'
			}
		},
		{
			title: 'Up to ₦3,000,000 3rd-party <br /> property damage',
			description:
				'Shield yourself from potential expenses resulting from damage to properties belonging to third parties, with coverage extending up to ₦3,000,000.',
			icon: {
				icon: 'house-storm',
				dir: 'random'
			}
		}
	]
</script>

<template>
	<div>
		<header class="bg-auto-800 px-5 flexed flex-col justify-center md:px-0 sm:pb-24 pt-10 sm:pt-24">
			<ui-pad
				:ui="{ paddingInline: 'sm:px-10', width: 'max-w-[1400px]' }"
				class="inline-flex items-center justify-between w-full lg:flex-nowrap flex-wrap gap-y-10"
			>
				<ui-h1
					tag="Mini Comprehensive"
					paragraph="Mini comprehensive has all benefits of the regular 3rd party cover but also covers your own vehicle to a limit of ₦500,000 in the event of an accident for just ₦25,000 per annum."
					:tag-icon="{ dir: 'random', icon: 'bus-white' }"
					:ui="btnClass"
					primary-btn-text="Protect your vehicle in 5 minutes <span class='max-sm:hidden'>or less</span>"
					secondary-btn-text="Speak to us to get on board"
					:action="{ type: 'onboard', product: ProductCode.MINI_COMPREHENSIVE }"
				>
					<template #title="{ highlightColor }">
						<span class="block w-fit text-wrap">
							Insurance that goes <br class="max-sm:hidden" />
							the <span :class="[highlightColor]"> extra mile</span>
						</span>
					</template>
				</ui-h1>

				<ui-image-frame
					alt="Business vehicle illustration representing mini comprehensive auto insurance"
					:src="{
						lg: '/MCG/random/premgurhwm5zjvf88ayb',
						sm: '/v1727082911/bbo_twyjxx.webp'
					}"
					:ui="{
						pictureWrapper: {
							translate: 'sm:translate-x-5 translate-x-3 md:translate-y-[20px]'
						},
						motionItem: {
							background: ''
						},
						wrapper: {
							base: 'max-w-[418.23px] relative max-sm:mx-auto '
						},
						img: {
							height: 'sm:max-h-[unset] xl:h-full sm:h-[15em   max-h-[300px]'
						}
					}"
				>
					<template #animate-icon>
						<tools-icon dir="random" icon="vehicle" />
					</template>
				</ui-image-frame>
			</ui-pad>
		</header>

		<ui-spacer />

		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-partners />
			<ui-spacer />
			<main-benefit-description
				:highlighted-benefits="benefitHighlights"
				title="Fully transparent. Completely flexible."
				class="space-y-5"
				:ui="{ li: { activeClass: 'border-auto-500', activeText: 'text-auto-500' } }"
			>
				<div class="max-w-[712px] w-full text-center mx-auto flexed flex-col">
					<p class="sm:text-xl text-base text-gray-600">
						The perfect coverage tailored to your auto insurance needs
					</p>
				</div>
			</main-benefit-description>
			<ui-spacer />

			<main-productivity-benefits
				title="Keeping your business and others safe on the road"
				description="Your staff drives confidently with our mini comprehensive auto insurance, ensuring safety for your business and others."
				:team-benefits="teamBenefits"
				product-name="mini comprehensive"
				:ui="{
					container: {
						btn: {
							base: 'bg-auto-500 min-h-[56px] hover:bg-auto-800 ',
							onHoverPseudo: 'hover:before:bg-auto-800'
						}
					}
				}"
				@trigger="showBenefits = true"
			>
				<template #hover-icon>
					<tools-icon dir="random" icon="car-wheel" width="20" height="20" />
				</template>
			</main-productivity-benefits>
			<ui-spacer />

			<lazy-main-benefit-list-display
				title="Get Covered, Get Going"
				description="Hit the road knowing that your vehicles are completely covered."
				product-category="auto"
				:product-code="ProductCode.MINI_COMPREHENSIVE"
				class="hidden lg:block"
				:sort="true"
				:exclude="[ProductCode.THIRD_PARTY]"
			/>
			<ui-spacer />

			<molecules-information-hint category="auto" />

			<ui-spacer />

			<molecules-process-steps
				title="Convenient. Efficient. Fast."
				description="Secure your vehicles in 3 easy steps!"
				:processes="onboardingProcesses"
				:ui="{ strokeText: { strokeColor: '#FFCA99', color: 'text-auto-100' }, header: { width: '' } }"
				@trigger="() => (isDialogOpen = true)"
			>
				<template #btn>
					<ui-btn
						:ui="{
							btn: {
								base: 'bg-auto-500 mt-8 min-h-[56px]',
								onHoverPseudo: 'hover:before:bg-auto-800'
							},
							text: {
								base: 'text-white'
							}
						}"
						@click="navigateToProduct(ProductCode.MINI_COMPREHENSIVE)"
					>
						<template #icon>
							<tools-icon dir="random" icon="car-wheel" width="20" height="20" />
						</template>
						<span>Get Covered Now</span>
					</ui-btn>
				</template>
			</molecules-process-steps>
		</ui-pad>
		<ui-spacer />

		<molecules-important-guides :guide-list="guideList" />
		<ui-spacer />

		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-testimonials />
		</ui-pad>
		<ui-spacer />

		<molecules-demo-card
			description=""
			title="Comprehensive coverage for your business."
			type="auto"
			@secondary-click="viewMoreProducts('auto')"
		/>

		<ui-spacer />

		<ui-pad :ui="{ paddingBlock: '' }">
			<molecules-investors class="pt-12 pb-20" />
			<main-faq :faq-list="faqList" />
		</ui-pad>
		<ui-spacer />

		<molecules-benefit-dialog
			v-model:show="showBenefits"
			:product-name="ProductCode.MINI_COMPREHENSIVE"
			product-category="auto"
		/>
		<molecules-auth-dialog v-model="isDialogOpen" selected="register" />
	</div>
</template>
